import Drawer, { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import toast from '@liquidweb-dev/undercurrent/toast';

import { FormSubmitHandler } from 'components/LWForm';
import UserForm, {
	UserFormValues,
} from 'components/standard/UserForm/UserForm';
import { useIsSelf } from 'modules/oidc/useAuthenticated';
import useAccountChatPasswordGet from 'modules/queries/account/chatPassword/useGet';
import useAccountChatPasswordSet from 'modules/queries/account/chatPassword/useSet';
import useAccountUserDetails from 'modules/queries/account/user/useDetails';
import useAccountUserUpdate from 'modules/queries/account/user/useUpdate';
import {
	checkHasOwnerRole,
	cleanRoles,
	cleanSelectableRoles,
} from 'modules/users';

export interface DrawerEditUserProps extends Omit<DrawerProps, 'onClose'> {
	onClose: () => void;
	username: string;
	/** if true, the passphrase input will be focused when the drawer opens */
	focusPassphrase?: boolean;
}

const DrawerEditUser = ({
	onClose,
	username,
	focusPassphrase,
	...drawerProps
}: DrawerEditUserProps) => {
	const isSelf = useIsSelf(username);

	const { mutateAsync: updateUser, isLoading: isLoadingUpdate } =
		useAccountUserUpdate();
	const { mutateAsync: setChatPassword, isLoading: isLoadingSetChatPassword } =
		useAccountChatPasswordSet();
	const { data: chatPasswordData } = useAccountChatPasswordGet();

	const { data: userData, isLoading: isLoadingUserDetails } =
		useAccountUserDetails({ username }, { enabled: drawerProps.open });

	const { email, firstname, lastname, roles: rolesRaw } = userData || {};

	const roles = cleanRoles(rolesRaw || []);
	const role = cleanSelectableRoles(rolesRaw || [])[0];

	const isAccountOwner = roles?.includes('AccountOwner');
	const restricted = !checkHasOwnerRole(rolesRaw || []);

	const handleSubmit: FormSubmitHandler<UserFormValues> = async (
		formValues,
	) => {
		const {
			email: formEmail,
			firstname: formFirstname,
			lastname: formLastname,
			role: formRole,
			username: formUsername,
			password,
			passphrase,
		} = formValues;

		const isUserModified =
			formUsername !== username ||
			formEmail !== email ||
			formFirstname !== firstname ||
			formLastname !== lastname ||
			formRole !== role ||
			password;

		const isPassphraseModified =
			isSelf && formValues.passphrase !== chatPasswordData?.password;

		const requests = [];

		if (isUserModified) {
			const payload = {
				email: formEmail,
				firstname: formFirstname,
				lastname: formLastname,
				roles: isAccountOwner ? ['AccountOwner'] : ['AccountLogin', formRole],
				username: formUsername,
				existing_user: username,
				password: password || undefined,
			};

			requests.push(
				updateUser(payload, {
					onError: () => toast.danger(`Unable to update user ${username}.`),
				}),
			);
		}

		if (isPassphraseModified) {
			requests.push(
				setChatPassword(
					{
						password: passphrase,
					},
					{
						onError: () =>
							toast.danger(`Unable to update ${username}'s passphrase.`),
					},
				),
			);
		}

		try {
			await Promise.all(requests);
			toast.success(`User ${formUsername} updated.`);
			onClose();
		} catch {
			// Do nothing
		}
	};

	return (
		<Drawer onClose={onClose} {...drawerProps}>
			<DrawerHeader onClose={onClose}>
				<Typography fontWeight={500} variant="title-xs">
					Edit user
				</Typography>
			</DrawerHeader>
			{isLoadingUserDetails ? (
				<DrawerContent>
					<Skeleton variant="rounded" width="100%" height="425px" />
				</DrawerContent>
			) : (
				<UserForm
					onClose={onClose}
					username={username}
					email={email || ''}
					firstname={firstname}
					lastname={lastname}
					role={role}
					roles={roles}
					onSubmit={handleSubmit}
					isSubmitting={isLoadingUpdate || isLoadingSetChatPassword}
					isEditing
					restricted={restricted}
					focusPassphrase={focusPassphrase && drawerProps.open}
				/>
			)}
		</Drawer>
	);
};

export default DrawerEditUser;
